import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Box, Icons, Text, Theme } from 'baukasten';

import messages from './messages';
import type { ThinkingStep } from '../types';
import { useProgressLabel } from '../hooks/useProgressLabel';
import { useThinkingTimings } from '../hooks/useThinkingTimings';
import { formatDuration } from '../utils/formatDuration';
import { ProgressHeadline } from './ProgressHeadline';
import { ToolStep } from './ToolStep';

const Toggle = styled.button`
  display: flex;
  align-items: center;
  gap: ${Theme.getBaseUnit(1)};
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

// the generated icons take only color/size, so the rotation lives on a wrapper
const Chevron = styled(Box)<{ $expanded: boolean }>`
  display: flex;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  transition: transform 120ms ease;
`;

const StepList = styled(Box)`
  > * + * {
    margin-top: ${Theme.getBaseUnit(2)};
  }
`;

type Props = {
  steps: ThinkingStep[];
  active: boolean;
  turnStartedAt: number | undefined;
};

export const ThinkingBlock = ({ steps, active, turnStartedAt }: Props) => {
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState(false);
  const { total, perStep, elapsed } = useThinkingTimings(
    steps,
    active,
    turnStartedAt
  );
  const progressLabel = useProgressLabel(steps);
  const hasSteps = steps.length > 0;

  if (!hasSteps && !active) return null;

  const getFinishedHeadline = () => {
    if (total === undefined) return formatMessage(messages.thoughtProcess);
    return formatMessage(messages.thoughtForSeconds, {
      duration: formatDuration(total),
    });
  };

  return (
    <Box mb={2}>
      <Toggle
        type="button"
        disabled={!hasSteps}
        aria-expanded={hasSteps ? expanded : undefined}
        onClick={() => setExpanded(current => !current)}
      >
        <Icons.Wand color="lilac-dark" size="16px" noPadding />
        {active ? (
          <ProgressHeadline label={progressLabel} elapsed={elapsed} />
        ) : (
          <Text fontSize={14} color="grey-dark">
            {getFinishedHeadline()}
          </Text>
        )}
        {hasSteps && (
          <Chevron $expanded={expanded}>
            <Icons.ChevronDown color="grey-dark" size="16px" noPadding />
          </Chevron>
        )}
      </Toggle>
      {expanded && (
        <StepList mt={2}>
          {steps.map(step => (
            <ToolStep key={step.id} step={step} duration={perStep[step.id]} />
          ))}
        </StepList>
      )}
    </Box>
  );
};
